import React, { Fragment, useRef, useContext, useEffect } from 'react'
import styled from 'styled-components'
import { useSlate, useReadOnly, ReactEditor } from 'vendor/slate-react'
import { Node, Transforms, Path } from 'vendor/slate'
import { ELEMENT_TYPES } from 'cg/components/documents/editor/slate/types'
import { EditorDispatch } from 'cg/components/documents/editor/slate/reducer'

import { insertTab } from 'cg/components/documents/editor/slate/plugins/tabs'

const ApiTabsHeaderItem = ({ attributes, children, element, editor }) => {
  const { state, dispatch } = useContext(EditorDispatch) || {}
  const { activeTabs } = state || {}
  const readOnly = useReadOnly()
  const { tabId, title } = element
  const path = ReactEditor.findPath(editor, element)
  const tabsHeaderPath = Node.has(editor, path) ? Path.parent(path) : null
  const tabsPath = tabsHeaderPath && Node.has(editor, tabsHeaderPath) ? Path.parent(tabsHeaderPath) : null
  const tabs = tabsPath && Node.has(editor, tabsPath) ? Node.get(editor, tabsPath) : null

  useEffect(() => {
    if (
      tabs &&
      tabs.children &&
      tabs.children[0] &&
      tabs.children[0].children &&
      tabs.children[0].children[0] &&
      tabs.defaultActiveTab !== tabs.children[0].children[0].tabId
    ) {
      Transforms.setNodes(editor, { defaultActiveTab: tabs.children[0].children[0].tabId }, { at: tabsPath })
    }
  }, [tabs, tabsPath, editor])

  const onClick = tabId => {
    if (tabs) {
      activeTabs[tabs.id] = tabId
      dispatch({ type: 'activateTab', payload: activeTabs })
    }
  }

  const isTabActive = activeTabs ? (tabs ? activeTabs[tabs.id] === tabId : false) : tabs ? tabs.defaultActiveTab === tabId : false

  return (
    <Tab active={isTabActive} contentEditable={false}>
      <>
        <TabTitleContainer
          active={isTabActive}
          onClick={() => {
            onClick(tabId)
          }}
        >
          <TabTitle {...attributes} active={isTabActive} readOnly={readOnly}>
            {children}
          </TabTitle>
        </TabTitleContainer>
      </>
    </Tab>
  )
}

export default ApiTabsHeaderItem

const TabAddItem = styled.div`
  width: 20px;
  height: 20px;
  font-size: 13px;
  border-radius: 4px;
  position: absolute;
  cursor: pointer;
  right: -10px;
  align-items: center;
  justify-content: center;
  background: ${props => props.theme.bgLightest};
  border: 1px solid ${props => props.theme.borderColor};
  color: ${props => props.theme.textGreyTinted};
  box-shadow: 1px 1px 10px rgba(0, 0, 0, 0.05);
  -webkit-transition: 0.15s;
  transition: 0.15s;
  display: none;
  z-index: 2;
  &:hover,
  &:active {
    background: ${props => props.theme.primary};
    color: ${props => props.theme.bgSecondLightest};
    border-color: ${props => props.theme.primary};
    box-shadow: none;
  }
`

const TabActionsButton = styled.div`
  background: ${props => props.theme.hoverBgLightestInverse};
  font-size: 13.5px;
  height: 21px;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  margin: 0px 2px 5px 2px;
  color: ${props => props.theme.textGreyTintedLighter};
  border-radius: 3px;
  padding: 4px;
  width: 100%;
  display: flex;
  -webkit-transition: 0.15s;
  visibility: hidden;
  &.is-dragging {
    visibility: hidden !important;
  }
  &:hover,
  &:active {
    background: ${props => props.theme.primary};
    color: ${props => props.theme.bgSecondLightest};
    border-color: ${props => props.theme.primary};
    box-shadow: none;
  }
`

const Tab = styled.li`
  flex: 1;
  max-width: 90%;
  font-size: 12.75px;
  display: flex;
  color: ${props => props.theme.textGreyTintedLighter};
  margin: 0px;
  font-weight: bold;
  overflow: ${props => (props.active ? 'visible' : 'hidden')};
  border-bottom: ${props => (props.active ? '1px solid transparent' : `1px solid ${props.theme.borderColor}`)};
  color: ${props => (props.active ? props.theme.textGreyTintedDarker : props.theme.textGreyTintedLighter)};
  min-width: ${props => (props.active ? '0px' : 'auto')};
  z-index: ${props => (props.active ? '1' : 'auto')};
  margin-bottom: ${props => (props.active ? '-1px' : '0px')};
  background: transparent;
  flex-direction: column;
  cursor: default;
  &:hover {
    color: ${props => props.theme.textGreyTintedDarker};
    ${TabActionsButton} {
      visibility: ${props => (props.active ? 'visible' : 'hidden')};
    }
    ${TabAddItem} {
      display: ${props => (props.active ? 'flex' : 'none')};
    }
  }

  &:last-child {
    > span {
      border-top-right-radius: 3px;
    }
  }
  &:first-child {
    > span {
      border-left: 1px solid ${props => props.theme.borderColor};
      border-top-left-radius: 3px;
    }
  }
  &:only-child {
    max-width: 100%;
  }
`

const TabTitleContainer = styled.span`
  overflow: ${props => (props.active ? 'visible' : 'hidden')};
  padding: ${props => (props.active ? '0px' : '10px 0px')};
  height: ${props => (props.active ? '40px' : '39px')};
  background: ${props => (props.active ? props.theme.bgLightest : props.theme.bgSecondLightest)};
  border-right: 1px solid ${props => props.theme.borderColor};
  border-top: 1px solid ${props => props.theme.borderColor};
  width: 100%;
  text-overflow: ellipsis;
  word-break: normal;
  white-space: pre;
  display: flex;
  align-items: center;
  position: relative;
}
`

const TabTitle = styled.div`
  padding: 0px 15px;
  overflow: hidden;
  max-width: 100%;
  text-overflow: ellipsis;
`
